import { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";

import AppShell from "@/components/AppShell";
import ProfileWorkspaceLayout from "@/components/ProfileWorkspaceLayout";
import { Button } from "@/components/ui/button";
import { deleteLeaveRequest, getLeaveBalance, getLeaveRequests, getMyProfile, getTaskUserSuggestions } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const statusMeta = {
  pending: "chip chip-warn",
  approved: "chip chip-ok",
  rejected: "chip chip-crit",
};

const toDate = (value) => {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatDateRange = (fromDate, toDateValue) => {
  if (!fromDate || !toDateValue) return "—";
  if (fromDate === toDateValue) return formatDate(fromDate);
  return `${formatDate(fromDate)} – ${formatDate(toDateValue)}`;
};

const sortedByFromDateDesc = (rows) => [...rows].sort((a, b) => String(b.from_date || "").localeCompare(String(a.from_date || "")));

const actorName = (email = "") => {
  const local = String(email).split("@")[0] || "";
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export default function ProfileLeavesPage({ user, onLogout }) {
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [deletingLeaveId, setDeletingLeaveId] = useState("");

  const isSuperAdmin = user.role === "super_admin";

  useEffect(() => {
    const load = async () => {
      try {
        const [balanceRes, leavesRes, profileRes, membersRes] = await Promise.all([
          getLeaveBalance(),
          getLeaveRequests(),
          getMyProfile(),
          getTaskUserSuggestions({ q: "", workspace_domain: user.workspace_domain }),
        ]);
        setBalance(balanceRes);
        setLeaves(sortedByFromDateDesc(leavesRes || []));
        setProfile(profileRes);
        setMembers(membersRes || []);
      } catch (error) {
        toast.error(error.message);
      }
    };
    load();
  }, [user.workspace_domain]);

  const myLeaves = useMemo(() => leaves.filter((row) => row.user_email === user.email), [leaves, user.email]);

  const currentFyRemaining = useMemo(() => {
    const paidBucket = balance?.casual;
    if (!paidBucket) return 0;
    return Math.max(0, Number(paidBucket.total || 0) - Number(paidBucket.used || 0));
  }, [balance]);

  const upcomingApproved = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return myLeaves.filter((row) => row.status === "approved" && toDate(row.from_date) && toDate(row.from_date) >= today);
  }, [myLeaves]);

  const approverLabel = useMemo(() => {
    const approverEmail = profile?.leave_approver_email;
    if (!approverEmail) return "Not assigned";
    if (approverEmail === user.email) return "Self (Super Admin)";
    const match = members.find((row) => row.email === approverEmail);
    return match?.name || actorName(approverEmail);
  }, [members, profile?.leave_approver_email, user.email]);

  const canDeleteLeave = (row) => {
    if (!row?.id) return false;
    if (isSuperAdmin) return true;
    if (row.user_email !== user.email) return false;
    const fromDate = toDate(row.from_date);
    if (!fromDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return fromDate >= today;
  };

  const deleteLeave = async (row) => {
    if (!row?.id) return;
    const confirmed = window.confirm("Delete this leave request?");
    if (!confirmed) return;

    setDeletingLeaveId(row.id);
    try {
      await deleteLeaveRequest(row.id);
      setLeaves((prev) => prev.filter((item) => item.id !== row.id));
      toast.success("Leave request deleted");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeletingLeaveId("");
    }
  };

  return (
    <AppShell user={user} onLogout={onLogout}>
      <ProfileWorkspaceLayout
        user={user}
        onLogout={onLogout}
        title="My Leaves"
        subtitle="Your leave balance, history and upcoming time off."
      >
        <section className="profile-kpi-grid" data-testid="profile-leaves-kpi-grid">
          <article className="profile-kpi-card green" data-testid="profile-leaves-kpi-fy-balance">
            <div className="label">FY Balance</div>
            <div className="value">{currentFyRemaining}</div>
            <div className="sub">of {Number(balance?.casual?.total || 24)} remaining</div>
            <div className="progress"><span style={{ width: `${Math.min(100, (currentFyRemaining / Math.max(1, Number(balance?.casual?.total || 24))) * 100)}%` }} /></div>
          </article>

          <article className="profile-kpi-card" data-testid="profile-leaves-kpi-used">
            <div className="label">Used this FY</div>
            <div className="value">{Number(balance?.casual?.used || 0)}</div>
            <div className="sub">Apr 25 – Mar 26</div>
          </article>

          <article className="profile-kpi-card" data-testid="profile-leaves-kpi-approved-ahead">
            <div className="label">Approved ahead</div>
            <div className="value">{upcomingApproved.length}</div>
            <div className="sub">{upcomingApproved.length ? "Upcoming leaves" : "No upcoming leaves"}</div>
          </article>

          <article className="profile-kpi-card indigo" data-testid="profile-leaves-kpi-birthday">
            <div className="label">Birthday leave</div>
            <div className="value">{Math.max(0, Number(balance?.birthday?.total || 1) - Number(balance?.birthday?.used || 0))}</div>
            <div className="sub">Auto on your birthday</div>
          </article>
        </section>

        <section className="profile-card" data-testid="profile-leaves-policy-strip">
          <div className="profile-policy-grid">
            <div>
              <div className="profile-field-label">Entitlement</div>
              <div className="profile-field-value">2 per month · 24/year</div>
            </div>
            <div>
              <div className="profile-field-label">Your approver</div>
              <div className="profile-field-value" data-testid="profile-leaves-approver-name">{approverLabel}</div>
            </div>
            <div>
              <div className="profile-field-label">Carry forward</div>
              <div className="profile-field-value">Within FY only</div>
            </div>
            <div>
              <div className="profile-field-label">Resets</div>
              <div className="profile-field-value">March 31, 2026</div>
            </div>
          </div>
        </section>

        <section className="profile-card" data-testid="profile-leaves-apply-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><CalendarDays className="h-4 w-4" /> Need time off?</div>
            <Button asChild data-testid="profile-leaves-apply-link-button">
              <a href="/hr">Apply for leave</a>
            </Button>
          </div>
          <p className="profile-field-note">Apply from the HR page — your request goes to your designated approver.</p>
        </section>

        <section className="profile-card" data-testid="profile-leaves-history-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><CalendarDays className="h-4 w-4" /> Leave history</div>
            <span className="chip chip-mute" data-testid="profile-leaves-history-fy-chip">FY 2025-26</span>
          </div>

          <div className="profile-table-wrap">
            <table className="profile-table" data-testid="profile-leaves-history-table">
              <thead>
                <tr>
                  <th>Dates</th>
                  <th>Type</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Actioned by</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {myLeaves.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty" data-testid="profile-leaves-history-empty">No leave records yet.</td>
                  </tr>
                ) : (
                  myLeaves.map((row) => (
                    <tr key={row.id} data-testid={`profile-leaves-history-row-${row.id}`}>
                      <td>{formatDateRange(row.from_date, row.to_date)}</td>
                      <td>{row.leave_type}</td>
                      <td>{row.days} day{Number(row.days) === 1 ? "" : "s"}</td>
                      <td><span className={statusMeta[row.status] || "chip chip-mute"}>{row.status}</span></td>
                      <td>
                        {row.manager_email
                          ? `${actorName(row.manager_email)} · ${formatDate(row.updated_at || row.created_at)}`
                          : row.status === "pending"
                            ? "Awaiting review"
                            : "—"}
                      </td>
                      <td>
                        {canDeleteLeave(row) ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => deleteLeave(row)}
                            disabled={deletingLeaveId === row.id}
                            data-testid={`profile-leaves-delete-button-${row.id}`}
                          >
                            {deletingLeaveId === row.id ? "Deleting..." : "Delete"}
                          </Button>
                        ) : (
                          <span className="chip chip-mute" data-testid={`profile-leaves-delete-locked-${row.id}`}>Locked</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </ProfileWorkspaceLayout>
    </AppShell>
  );
}
